import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Users, ShoppingCart, TrendingDown, Coins, Package, Heart, 
  ArrowRight, Check, X, Shield, Briefcase, Truck, Star,
  Award, Calendar, Home, DollarSign, UserPlus, Settings,
  BarChart, Clock, MapPin, Zap, Gift, BookOpen, Store
} from 'lucide-react';

const Tutorial = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [currentSection, setCurrentSection] = useState(0);
  const [hasSeenTutorial, setHasSeenTutorial] = useState(false);

  useEffect(() => {
    const seen = localStorage.getItem('tutorial_completed');
    if (seen === 'true') {
      setHasSeenTutorial(true);
    }
  }, []);

  const completeTutorial = () => {
    localStorage.setItem('tutorial_completed', 'true');
    navigate('/home');
  };

  const skipTutorial = () => {
    localStorage.setItem('tutorial_completed', 'skipped');
    navigate('/home');
  };

  const tutorialSections = [
    {
      title: "Getting Started",
      steps: [
        {
          icon: <Users className="w-12 h-12 text-blue-600" />,
          title: "Welcome to UniLedge!",
          description: "The Economic Coordination Platform",
          details: [
            "Save 25-40% through group purchasing",
            "No membership fees ever",
            "Support local vendors and neighbors",
            "Build community wealth together"
          ]
        },
        {
          icon: <TrendingDown className="w-12 h-12 text-green-600" />,
          title: "How Group Buying Works",
          description: "More members = Better prices for everyone",
          details: [
            "5-10 people: Save 20% off retail",
            "11-25 people: Save 30% off retail", 
            "26+ people: Save 35-40% off retail",
            "Price drops automatically as groups grow"
          ]
        },
        {
          icon: <ShoppingCart className="w-12 h-12 text-purple-600" />,
          title: "Shopping Process",
          description: "Simple steps to massive savings",
          details: [
            "Browse or search for groups",
            "Join before the deadline",
            "Commit to purchase quantity",
            "Payment processes at final price",
            "Pick up or get delivered"
          ]
        }
      ]
    },
    {
      title: "Trust & Reliability",
      steps: [
        {
          icon: <Shield className="w-12 h-12 text-indigo-600" />,
          title: "Trust Score System",
          description: "Your reputation on the platform",
          details: [
            "Start at 3.0 trust score",
            "Increases with successful transactions",
            "Higher scores unlock more features",
            "3.5+ can coordinate groups",
            "4.0+ can become vendors"
          ]
        },
        {
          icon: <Star className="w-12 h-12 text-yellow-600" />,
          title: "Building Your Trust",
          description: "Ways to increase your score",
          details: [
            "Complete purchases on time (+0.2)",
            "Pick up orders promptly (+0.1)",
            "Complete logistics jobs (+0.3)",
            "Get positive reviews (+0.1-0.3)",
            "Verify your identity (+0.3)"
          ]
        },
        {
          icon: <Award className="w-12 h-12 text-green-600" />,
          title: "Trust Badges",
          description: "Recognition for reliable members",
          details: [
            "Reliable Member: 3.5+ score",
            "Trusted Coordinator: 4.0+ score",
            "Community Leader: 4.5+ score",
            "Platform Champion: 4.8+ score",
            "Badges shown on your profile"
          ]
        }
      ]
    },
    {
      title: "Token Economy",
      steps: [
        {
          icon: <Coins className="w-12 h-12 text-yellow-600" />,
          title: "Earning Tokens",
          description: "Multiple ways to earn rewards",
          details: [
            "Join groups: 5-10 tokens",
            "Complete purchases: 10-50 tokens",
            "Refer friends: 25 tokens each",
            "Create successful groups: 50-200 tokens",
            "Complete deliveries: 20-100 tokens"
          ]
        },
        {
          icon: <Gift className="w-12 h-12 text-purple-600" />,
          title: "Token Bonuses",
          description: "Multiply your earnings",
          details: [
            "Bulk orders: 25-50% bonus tokens",
            "Early group joining: 2x tokens",
            "Group creator rewards: Up to 200 tokens",
            "Milestone achievements: Special bonuses",
            "Seasonal promotions: Extra tokens"
          ]
        },
        {
          icon: <DollarSign className="w-12 h-12 text-green-600" />,
          title: "Using Your Tokens",
          description: "Tokens have real value",
          details: [
            "1 token = $1 platform credit",
            "Use for discounts on purchases",
            "Transfer to other members",
            "Convert to vendor credit",
            "Future: Cash out option"
          ]
        }
      ]
    },
    {
      title: "Skills & Resources",
      steps: [
        {
          icon: <Briefcase className="w-12 h-12 text-blue-600" />,
          title: "Skills System",
          description: "Turn your abilities into income",
          details: [
            "Verify skills in 3 tiers",
            "Self-reported: Instant (3.0+ trust)",
            "Document verified: 1-2 days (3.5+ trust)",
            "Professional: 2-3 days (4.0+ trust)",
            "Unlock job opportunities"
          ]
        },
        {
          icon: <Truck className="w-12 h-12 text-orange-600" />,
          title: "Resource Registration",
          description: "Share vehicles, spaces, equipment",
          details: [
            "Register pickup trucks, vans",
            "List storage spaces available",
            "Share equipment (dollies, freezers)",
            "Set availability schedules",
            "Earn tokens for resource use"
          ]
        },
        {
          icon: <Calendar className="w-12 h-12 text-purple-600" />,
          title: "Platform Jobs",
          description: "Earn by helping the community",
          details: [
            "Delivery coordinator: 50-100 tokens",
            "Group organizer: 30-80 tokens",
            "Pickup assistant: 20-40 tokens",
            "Quality checker: 30-60 tokens",
            "Jobs matched to your skills"
          ]
        }
      ]
    },
    {
      title: "Groups & Coordination",
      steps: [
        {
          icon: <UserPlus className="w-12 h-12 text-blue-600" />,
          title: "Joining Groups",
          description: "Find and participate in savings",
          details: [
            "Browse by category or search",
            "Check group progress and deadline",
            "See current and next price breaks",
            "Join with one click",
            "Track in 'My Groups' section"
          ]
        },
        {
          icon: <Home className="w-12 h-12 text-green-600" />,
          title: "Creating Groups",
          description: "Become a group organizer",
          details: [
            "Need 3.5+ trust score",
            "Select products and vendors",
            "Set minimum/maximum participants",
            "Define timeline and logistics",
            "Earn creator rewards"
          ]
        },
        {
          icon: <Settings className="w-12 h-12 text-gray-600" />,
          title: "Managing Groups",
          description: "Admin tools for organizers",
          details: [
            "Post logistics jobs",
            "Send announcements",
            "Track member participation",
            "Coordinate pickups/deliveries",
            "Access analytics dashboard"
          ]
        }
      ]
    },
    {
      title: "Logistics & Delivery",
      steps: [
        {
          icon: <MapPin className="w-12 h-12 text-red-600" />,
          title: "Pickup Options",
          description: "Convenient collection points",
          details: [
            "Central neighborhood locations",
            "Scheduled pickup windows",
            "Text notifications when ready",
            "Earn 2 extra tokens for pickup",
            "Help others pickup for tokens"
          ]
        },
        {
          icon: <Package className="w-12 h-12 text-indigo-600" />,
          title: "Delivery Service",
          description: "Get orders delivered",
          details: [
            "Small delivery fee ($5-10)",
            "Community members deliver",
            "Track delivery status",
            "Rate your delivery experience",
            "Become a delivery driver"
          ]
        },
        {
          icon: <Clock className="w-12 h-12 text-purple-600" />,
          title: "Timeline & Tracking",
          description: "Stay informed at every step",
          details: [
            "Group forming: Join phase",
            "Deadline reached: Price finalized",
            "Payment processed: Automatic",
            "Order placed: With vendor",
            "Ready for pickup: Notified"
          ]
        }
      ]
    },
    {
      title: "Vendors & Business",
      steps: [
        {
          icon: <Store className="w-12 h-12 text-blue-600" />,
          title: "Vendor Participation",
          description: "How businesses work with us",
          details: [
            "Direct sales to groups",
            "No middleman fees",
            "85% revenue share",
            "Weekly payments",
            "Access to bulk buyers"
          ]
        },
        {
          icon: <BarChart className="w-12 h-12 text-green-600" />,
          title: "Vendor Benefits",
          description: "Why vendors love UniLedge",
          details: [
            "Guaranteed bulk orders",
            "Reduced marketing costs",
            "Direct customer relationships",
            "Predictable demand",
            "Community support"
          ]
        },
        {
          icon: <Zap className="w-12 h-12 text-yellow-600" />,
          title: "Becoming a Vendor",
          description: "Join as a business",
          details: [
            "Need 3.5+ trust score",
            "Business verification required",
            "Set your bulk pricing tiers",
            "Create product listings",
            "Receive direct payments"
          ]
        }
      ]
    },
    {
      title: "Community Impact",
      steps: [
        {
          icon: <Heart className="w-12 h-12 text-red-600" />,
          title: "Local Economic Impact",
          description: "Keep money in your community",
          details: [
            "93% of money stays local",
            "Creates neighborhood jobs",
            "Supports local vendors",
            "Builds community connections",
            "Reduces corporate extraction"
          ]
        },
        {
          icon: <Users className="w-12 h-12 text-purple-600" />,
          title: "Social Benefits",
          description: "More than just savings",
          details: [
            "Meet your neighbors",
            "Build trusted relationships",
            "Share resources efficiently",
            "Help those in need",
            "Strengthen community bonds"
          ]
        },
        {
          icon: <BookOpen className="w-12 h-12 text-blue-600" />,
          title: "Your Role",
          description: "Everyone contributes to success",
          details: [
            "Share with friends and family",
            "Provide feedback for improvement",
            "Help new members learn",
            "Participate in governance",
            "Build the future together"
          ]
        }
      ]
    }
  ];

  const sections = tutorialSections[currentSection];
  const currentStepData = sections.steps[currentStep];
  const totalSteps = sections.steps.length;
  const totalSections = tutorialSections.length;

  const handleNext = () => {
    if (currentStep < totalSteps - 1) {
      setCurrentStep(currentStep + 1);
    } else if (currentSection < totalSections - 1) {
      setCurrentSection(currentSection + 1);
      setCurrentStep(0);
    } else {
      completeTutorial();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    } else if (currentSection > 0) {
      setCurrentSection(currentSection - 1);
      setCurrentStep(tutorialSections[currentSection - 1].steps.length - 1);
    }
  };

  if (hasSeenTutorial) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <Check className="w-16 h-16 text-green-600 mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-2">You're All Set!</h2>
          <p className="text-gray-600 mb-6">
            You've already completed the tutorial. Ready to start saving?
          </p>
          <button
            onClick={() => navigate('/home')}
            className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700"
          >
            Go to Homepage
          </button>
          <button
            onClick={() => {
              setHasSeenTutorial(false);
              setCurrentStep(0);
              setCurrentSection(0);
            }}
            className="w-full mt-3 text-blue-600 hover:text-blue-800"
          >
            Watch Tutorial Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-sm text-gray-500">{sections.title}</h2>
              <p className="text-xs text-gray-400 mt-1">
                Section {currentSection + 1} of {totalSections} • 
                Step {currentStep + 1} of {totalSteps}
              </p>
            </div>
            <button
              onClick={skipTutorial}
              className="text-gray-500 hover:text-gray-700 flex items-center text-sm"
            >
              Skip Tutorial
              <X className="w-4 h-4 ml-1" />
            </button>
          </div>
          
          {/* Progress Bar */}
          <div className="mt-4 flex space-x-1">
            {tutorialSections.map((_, sectionIndex) => (
              <div key={sectionIndex} className="flex-1">
                <div className="h-1 bg-gray-200 rounded-full overflow-hidden">
                  <div 
                    className={`h-full transition-all ${
                      sectionIndex < currentSection 
                        ? 'bg-green-500 w-full'
                        : sectionIndex === currentSection
                        ? 'bg-blue-600'
                        : 'bg-gray-200 w-0'
                    }`}
                    style={{
                      width: sectionIndex === currentSection 
                        ? `${((currentStep + 1) / totalSteps) * 100}%`
                        : undefined
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex flex-col items-center justify-center px-4 py-12">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-2xl w-full">
          {/* Icon */}
          <div className="flex justify-center mb-6">
            {currentStepData.icon}
          </div>

          {/* Title */}
          <h1 className="text-2xl font-bold text-center mb-4">
            {currentStepData.title}
          </h1>

          {/* Description */}
          <p className="text-gray-600 text-center mb-6">
            {currentStepData.description}
          </p>

          {/* Details */}
          <div className="space-y-3 mb-8 max-w-md mx-auto">
            {currentStepData.details.map((detail, index) => (
              <div key={index} className="flex items-start">
                <Check className="w-5 h-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
                <span className="text-gray-700">{detail}</span>
              </div>
            ))}
          </div>

          {/* Navigation */}
          <div className="flex gap-3 max-w-md mx-auto">
            <button
              onClick={handleBack}
              disabled={currentSection === 0 && currentStep === 0}
              className="flex-1 py-3 border border-gray-300 rounded-lg font-semibold hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Back
            </button>
            
            <button
              onClick={handleNext}
              className={`flex-1 py-3 rounded-lg font-semibold flex items-center justify-center ${
                currentSection === totalSections - 1 && currentStep === totalSteps - 1
                  ? 'bg-green-600 hover:bg-green-700 text-white'
                  : 'bg-blue-600 hover:bg-blue-700 text-white'
              }`}
            >
              {currentSection === totalSections - 1 && currentStep === totalSteps - 1 ? (
                'Start Using UniLedge!'
              ) : (
                <>
                  Next
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </button>
          </div>
        </div>

        {/* Section Navigation */}
        <div className="mt-8 flex flex-wrap justify-center gap-2 max-w-2xl">
          {tutorialSections.map((section, index) => (
            <button
              key={index}
              onClick={() => {
                setCurrentSection(index);
                setCurrentStep(0);
              }}
              className={`px-3 py-1 text-xs rounded-full transition-colors ${
                index === currentSection
                  ? 'bg-blue-600 text-white'
                  : index < currentSection
                  ? 'bg-green-100 text-green-700'
                  : 'bg-gray-100 text-gray-500'
              }`}
            >
              {section.title}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Tutorial;